import type {
  DealResourcePlan,
  ResourcePlanPricingBasis,
  StaffingPlanEntry,
} from '@protoss/shared';
import {
  formatCurrencyAmount,
  matchRoleKey,
  readRoleAllocation,
  readRolePricing,
  resolveRoleCost,
  resolveRoleCostSplit,
  roleUnitRate,
} from '@protoss/shared';
import {
  Button,
  ChevronDownIcon,
  Cross1Icon,
  DropdownMenu,
  GlobeIcon,
  HomeIcon,
  IconSize,
  LockClosedIcon,
  NumberInput,
  Tooltip,
} from '@protoss/ui';
import * as React from 'react';
import type { ResourcePlanAvailableRole } from '../services/salesApi';

export interface ResourceInvestmentTableProps {
  plan: DealResourcePlan;
  hideWeeklyAvgColumn?: boolean;
  onUpdateRolePricingBasis?: (
    roleId: string,
    pricingBasis: ResourcePlanPricingBasis,
    offshorePercent?: number
  ) => void;
  regenerating?: boolean;
  updatingRolePricingBasisId?: string | null;
  /** Override a role's allocation % (0–100), or `null` to clear it. */
  onUpdateRoleAllocation?: (roleId: string, allocationPercent: number | null) => void;
  updatingRoleAllocationId?: string | null;
  discountPercentageOverride?: number | null;
  defaultDiscountPercentage?: number | null;
  onDiscountOverride?: (pct: number | null) => void;
  /**
   * The workspace role catalog for the add/remove role multi-select. When an
   * `onRoleSelectionChange` handler is given (editable Estimate pane) the picker
   * renders; absent (read-only Resource Plan pane) it does not.
   */
  availableRoles?: ResourcePlanAvailableRole[];
  /** Set the full desired role roster (current roles +/- the user's edits). */
  onRoleSelectionChange?: (roleIds: string[]) => void;
  /** True while a roster change is being saved (disables the picker). */
  updatingRoles?: boolean;
}

const PRICING_BASIS_OPTIONS = [
  { value: 'onshore', label: 'Onshore' },
  { value: 'offshore', label: 'Offshore' },
  { value: 'hybrid', label: 'Hybrid' },
] as const;

/**
 * Canonical size_tiered phase keys with dedicated `[data-phase='…']` chip
 * colors. Custom (flat_percentage) keys fall back to an index-based palette via
 * `data-phase-index`; gating on this set keeps the two from colliding.
 */
const KNOWN_PHASE_KEYS = new Set(['discovery', 'design', 'build', 'test', 'deploy', 'hypercare']);

/** Color attribute for a chip: known keys keep `data-phase`; custom keys add an index. */
function phaseIndexAttr(key: string, idx: number): { 'data-phase-index'?: number } {
  return KNOWN_PHASE_KEYS.has(key) ? {} : { 'data-phase-index': idx % 6 };
}

function getRolePricingBasis(plan: DealResourcePlan, roleId: string): ResourcePlanPricingBasis {
  return readRolePricing(plan.rolePricingBasisByRoleId, roleId).basis;
}

function getRoleOffshorePercent(plan: DealResourcePlan, roleId: string): number {
  return readRolePricing(plan.rolePricingBasisByRoleId, roleId).offshorePercent;
}

function getRoleCost(plan: DealResourcePlan, entry: StaffingPlanEntry): number {
  return resolveRoleCost(
    entry,
    getRolePricingBasis(plan, entry.role_id),
    getRoleOffshorePercent(plan, entry.role_id)
  );
}

function getDisplayedTotalCost(plan: DealResourcePlan): number {
  return plan.resourcingSnapshot.staffing_plan.reduce(
    (sum, entry) => sum + getRoleCost(plan, entry),
    0
  );
}

/**
 * Compact inline offshore-% editor shown only when a role is set to Hybrid.
 * Holds a local draft and commits on blur / Enter to avoid an API call per
 * keystroke; the committed value is clamped to 0–100.
 */
function HybridOffshoreInput({
  value,
  disabled,
  onCommit,
}: {
  value: number;
  disabled?: boolean;
  onCommit: (offshorePercent: number) => void;
}) {
  const [draft, setDraft] = React.useState<number | string>(value);

  React.useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = () => {
    const parsed = typeof draft === 'string' ? parseFloat(draft) : draft;
    const clamped = Number.isNaN(parsed) ? value : Math.max(0, Math.min(100, parsed));
    setDraft(clamped);
    if (clamped !== value) onCommit(clamped);
  };

  return (
    <span className="resource-investment-table__hybrid-input">
      <input
        type="text"
        inputMode="numeric"
        className="resource-investment-table__hybrid-number"
        disabled={disabled}
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        aria-label="Offshore percentage"
      />
      <span className="resource-investment-table__hybrid-suffix">%</span>
    </span>
  );
}

const PRICING_BASIS_LABELS: Record<ResourcePlanPricingBasis, string> = {
  onshore: 'Onshore',
  offshore: 'Offshore',
  hybrid: 'Hybrid',
};

/**
 * Collapsible pricing-basis selector. Collapsed, it shows only the selected
 * pill; clicking it expands the remaining options to the right (animated).
 * Choosing an option collapses back to the single chosen pill. When Hybrid is
 * selected, the offshore-% editor sits alongside the pill. Outside pointerdown
 * or Escape collapses. When `disabled`, it is a static, non-interactive pill.
 */
function PricingBasisControl({
  value,
  offshorePercent,
  disabled,
  onChange,
}: {
  value: ResourcePlanPricingBasis;
  offshorePercent: number;
  disabled?: boolean;
  onChange: (basis: ResourcePlanPricingBasis, offshorePercent?: number) => void;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Collapse on outside pointerdown (mirrors TagSelectField's pattern).
  React.useEffect(() => {
    if (!expanded) return;
    const handler = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [expanded]);

  const select = (basis: ResourcePlanPricingBasis) => {
    setExpanded(false);
    if (basis !== value) {
      // Omit the percent on switch-to-hybrid so the handler seeds the configured
      // default offshore %. Edits to the % go through HybridOffshoreInput.
      onChange(basis);
    }
  };

  // Selected pill stays leftmost; the rest follow in canonical order.
  const rest = PRICING_BASIS_OPTIONS.filter((o) => o.value !== value);

  return (
    <div
      ref={containerRef}
      className={
        'resource-investment-table__basis-control' +
        (expanded ? ' resource-investment-table__basis-control--expanded' : '')
      }
      onKeyDown={(e) => {
        if (e.key === 'Escape') setExpanded(false);
      }}
    >
      <div className="resource-investment-table__basis-pills" role="radiogroup">
        <button
          type="button"
          role="radio"
          aria-checked={true}
          aria-expanded={expanded}
          disabled={disabled}
          data-basis={value}
          className="resource-investment-table__basis-pill resource-investment-table__basis-pill--selected"
          onClick={() => {
            if (disabled) return;
            setExpanded((prev) => !prev);
          }}
        >
          {PRICING_BASIS_LABELS[value]}
        </button>
        <div className="resource-investment-table__basis-rest" aria-hidden={!expanded}>
          {rest.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={false}
              tabIndex={expanded ? 0 : -1}
              disabled={disabled || !expanded}
              data-basis={option.value}
              className="resource-investment-table__basis-pill"
              onClick={() => select(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {value === 'hybrid' && (
        <HybridOffshoreInput
          value={offshorePercent}
          disabled={disabled}
          onCommit={(pct) => onChange('hybrid', pct)}
        />
      )}
    </div>
  );
}

/**
 * Muted onshore/offshore breakdown caption: Home icon = onshore, Globe icon =
 * offshore. Reuses the same icon semantics as ResourcingModelSummary.
 */
function HomeGlobeSplit({
  onshore,
  offshore,
  className,
}: {
  onshore: React.ReactNode;
  offshore: React.ReactNode;
  className?: string;
}) {
  return (
    <span className={'resource-investment-table__split' + (className ? ' ' + className : '')}>
      <span className="resource-investment-table__split-part">
        <HomeIcon className="resource-investment-table__split-icon" aria-label="Onshore" />
        {onshore}
      </span>
      <span className="resource-investment-table__split-part">
        <GlobeIcon className="resource-investment-table__split-icon" aria-label="Offshore" />
        {offshore}
      </span>
    </span>
  );
}

/** Humanize a section key for display, e.g. "data_migration" → "Data Migration". */
const labelize = (key: string): string =>
  key.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * The muted allocation indicator next to a role's hours. Three shapes:
 *  - section-override role ⇒ a lock icon whose tooltip lists the source sections
 *    (its hours are owned by the estimate, not editable here);
 *  - editable role ⇒ a click-to-edit % (mirrors the estimate's SectionHoursEdit:
 *    commit on blur/Enter, spinner while saving, amber + ✕ reset when overridden);
 *  - read-only role (no handler) ⇒ a plain muted %.
 */
function RoleAllocationControl({
  percent,
  isOverridden,
  overrideSectionKeys,
  saving,
  onSave,
}: {
  percent: number; // 0–1 fraction
  isOverridden: boolean;
  overrideSectionKeys?: string[];
  saving: boolean;
  onSave?: (allocationPercent: number | null) => void;
}) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const pct = Math.round(percent * 100);

  if (overrideSectionKeys && overrideSectionKeys.length > 0) {
    return (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span
            className="resource-investment-table__alloc-lock"
            aria-label="Hours assigned from an estimate section"
          >
            <LockClosedIcon aria-hidden="true" />
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content side="top" sideOffset={4} className="ui-tooltip">
            Direct from {overrideSectionKeys.map(labelize).join(', ')}
            <Tooltip.Arrow className="ui-tooltip-arrow" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    );
  }

  if (saving) {
    return (
      <span className="resource-investment-table__alloc">
        <span className="resource-investment-table__alloc-pct">{pct}%</span>
        <span className="inline-edit__spinner" aria-label="Saving…" />
      </span>
    );
  }

  if (editing) {
    const commit = () => {
      setEditing(false);
      const n = parseInt(draft, 10);
      if (!Number.isNaN(n)) {
        const clamped = Math.max(0, Math.min(100, n));
        if (clamped !== pct) onSave?.(clamped);
      }
    };
    return (
      <span className="resource-investment-table__alloc resource-investment-table__alloc--edit">
        <input
          type="text"
          inputMode="numeric"
          autoFocus
          className="resource-investment-table__alloc-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
            if (e.key === 'Escape') setEditing(false);
          }}
          aria-label="Allocation percentage"
        />
        <span className="resource-investment-table__alloc-suffix">%</span>
      </span>
    );
  }

  if (!onSave) {
    return <span className="resource-investment-table__alloc-pct">{pct}%</span>;
  }

  return (
    <span className="resource-investment-table__alloc">
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            className={
              'resource-investment-table__alloc-pct resource-investment-table__alloc-pct--btn' +
              (isOverridden ? ' resource-investment-table__alloc-pct--overridden' : '')
            }
            onClick={() => {
              setDraft(String(pct));
              setEditing(true);
            }}
          >
            {pct}%
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content side="top" sideOffset={4} className="ui-tooltip">
            Click to override allocation %
            <Tooltip.Arrow className="ui-tooltip-arrow" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
      {isOverridden ? (
        <button
          type="button"
          className="resource-investment-table__alloc-clear"
          aria-label="Reset to calculated allocation"
          onClick={() => onSave(null)}
        >
          <Cross1Icon width={8} height={8} />
        </button>
      ) : null}
    </span>
  );
}

export function ResourceInvestmentTable({
  plan,
  hideWeeklyAvgColumn = false,
  onUpdateRolePricingBasis,
  regenerating,
  updatingRolePricingBasisId,
  onUpdateRoleAllocation,
  updatingRoleAllocationId,
  discountPercentageOverride,
  defaultDiscountPercentage,
  onDiscountOverride,
  availableRoles,
  onRoleSelectionChange,
  updatingRoles,
}: ResourceInvestmentTableProps) {
  const snap = plan.resourcingSnapshot;
  const displayedTotalCost = getDisplayedTotalCost(plan);
  // Base for the allocation % = the frozen grand total. The footer stays pinned to
  // it; only individual rows reflect an allocation override.
  const allocationBase = snap.totals.total_hours;
  // Sum of every role's effective allocation fraction; > 1 ⇒ over-allocated,
  // < 1 ⇒ under-allocated (e.g. after removing a role).
  const totalAllocationPct = snap.staffing_plan.reduce(
    (sum, e) =>
      sum + readRoleAllocation(plan.rolePricingBasisByRoleId, e.role_id, e, allocationBase).percent,
    0
  );
  const overAllocatedPct = Math.round((totalAllocationPct - 1) * 100);
  const underAllocatedPct = Math.round((1 - totalAllocationPct) * 100);

  // Role multi-select: options = the workspace catalog, selection = the roles
  // currently shown. Only rendered when an edit handler is supplied (Estimate pane)
  // AND the plan is an `ai` model - the roster is free-form only for `ai`. For
  // size_tiered/flat_percentage the roster is config-driven and add/remove toggles
  // are a backend no-op (rebuildAiRosterResourcing runs for `ai` plans only), so the
  // picker is hidden rather than presented as editable-but-dead.
  const showRolePicker =
    !!onRoleSelectionChange &&
    (availableRoles?.length ?? 0) > 0 &&
    snap.distribution_model === 'ai';
  // The catalog may spell a role `lead_consultant` while the plan staffs it as
  // `lead-consultant` (the dispatcher aligns `-`/`_`). Use the plan's spelling for an
  // option the plan already staffs, so it shows as selected and a toggle removes it
  // instead of adding a second copy. One option per role.
  const roleOptions = React.useMemo(() => {
    const planIds = snap.staffing_plan.map((e) => e.role_id);
    const seen = new Set<string>();
    return (availableRoles ?? []).flatMap((r) => {
      const value = matchRoleKey(r.role_id, planIds).match ?? r.role_id;
      if (seen.has(value)) return [];
      seen.add(value);
      return [{ value, label: r.role_name }];
    });
  }, [availableRoles, snap.staffing_plan]);
  const selectedRoleIds = React.useMemo(
    () => snap.staffing_plan.map((e) => e.role_id),
    [snap.staffing_plan]
  );
  // Section-override roles are locked (hours owned by the estimate) - keep them in
  // any selection the user submits so the picker can't drop them.
  const lockedRoleIds = React.useMemo(
    () =>
      snap.staffing_plan
        .filter((e) => (e.override_section_keys?.length ?? 0) > 0)
        .map((e) => e.role_id),
    [snap.staffing_plan]
  );
  // Dynamic phase chips from the plan's own phase list (custom phases supported).
  const phaseChips = (Array.isArray(snap.phases) ? snap.phases : []).map((p) => ({
    key: p.key,
    label: p.label,
  }));

  const effectiveDiscountPct =
    discountPercentageOverride ??
    defaultDiscountPercentage ??
    snap.totals.discount?.percentage ??
    0;

  const [isDiscountFormVisible, setIsDiscountFormVisible] = React.useState(
    effectiveDiscountPct > 0
  );
  const [localDiscountPct, setLocalDiscountPct] = React.useState<number | string>(
    effectiveDiscountPct
  );
  const [isApplyingDiscount, setIsApplyingDiscount] = React.useState(false);

  React.useEffect(() => {
    setIsDiscountFormVisible(effectiveDiscountPct > 0);
    setLocalDiscountPct(effectiveDiscountPct);
  }, [effectiveDiscountPct]);

  const handleDiscountApply = async () => {
    if (onDiscountOverride) {
      const val =
        typeof localDiscountPct === 'string' ? parseFloat(localDiscountPct) : localDiscountPct;
      try {
        setIsApplyingDiscount(true);
        if (isNaN(val)) {
          await onDiscountOverride(null);
        } else {
          await onDiscountOverride(Math.max(0, Math.min(100, val)));
        }
      } finally {
        setIsApplyingDiscount(false);
      }
    }
  };

  const handleDiscountRemove = async () => {
    if (onDiscountOverride) {
      if (effectiveDiscountPct !== 0) {
        try {
          setIsApplyingDiscount(true);
          await onDiscountOverride(0);
        } finally {
          setIsApplyingDiscount(false);
        }
      } else {
        setIsDiscountFormVisible(false);
        setLocalDiscountPct(0);
      }
    }
  };

  const isDiscountChanged = String(localDiscountPct) !== String(effectiveDiscountPct);
  const discountAmount = isDiscountFormVisible
    ? displayedTotalCost * (effectiveDiscountPct / 100)
    : 0;

  // Plan 73: engine-recorded integrity warnings (e.g. the plan staffs fewer hours
  // than the estimate because profile roles are missing from the rate card).
  const integrityWarnings = snap.warnings ?? [];

  return (
    <>
      {integrityWarnings.length > 0 && (
        <div className="resource-investment-table__integrity" role="alert">
          {integrityWarnings.map((w) => (
            <div key={w.code}>
              <strong>{w.severity === 'error' ? 'Pricing warning: ' : 'Note: '}</strong>
              {w.message}
            </div>
          ))}
        </div>
      )}
      {showRolePicker && (
        <div className="resource-investment-table__roles-bar">
          <span className="resource-investment-table__roles-label">Roles</span>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="resource-investment-table__roles-trigger"
                disabled={regenerating || updatingRoles}
              >
                <span>{selectedRoleIds.length} selected</span>
                <ChevronDownIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className="dropdown-content" sideOffset={4} align="end">
                {roleOptions.map((opt) => {
                  const isSelected = selectedRoleIds.includes(opt.value);
                  const isLocked = lockedRoleIds.includes(opt.value);
                  return (
                    <DropdownMenu.Item
                      key={opt.value}
                      className="dropdown-item"
                      disabled={isLocked}
                      onSelect={(e) => {
                        e.preventDefault();
                        if (isLocked) return;
                        const next = new Set(selectedRoleIds);
                        if (next.has(opt.value)) next.delete(opt.value);
                        else next.add(opt.value);
                        onRoleSelectionChange?.([...new Set([...next, ...lockedRoleIds])]);
                      }}
                    >
                      <span className="resource-investment-table__roles-check" aria-hidden="true">
                        {isSelected ? '✓' : ''}
                      </span>
                      {opt.label}
                    </DropdownMenu.Item>
                  );
                })}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      )}
      {/* Desktop Table View */}
      <table className="resource-investment-table">
        <thead>
          <tr>
            <th className="resource-investment-table__th">Role</th>
            <th className="resource-investment-table__th resource-investment-table__th--basis">
              Pricing Basis
            </th>
            <th className="resource-investment-table__th resource-investment-table__th--right">
              Total Hours
            </th>
            {!hideWeeklyAvgColumn && (
              <th className="resource-investment-table__th resource-investment-table__th--center">
                Weekly Avg by Phase
              </th>
            )}
            <th className="resource-investment-table__th resource-investment-table__th--right">
              Cost
            </th>
          </tr>
        </thead>
        <tbody>
          {snap.staffing_plan.map((entry) => {
            const selectedPricingBasis = getRolePricingBasis(plan, entry.role_id);
            const offshorePercent = getRoleOffshorePercent(plan, entry.role_id);
            const isUpdatingRole = updatingRolePricingBasisId === entry.role_id;
            const isHybrid = selectedPricingBasis === 'hybrid';
            // Allocation override re-slices this row only: effective hours + a
            // proportionally-scaled cost. The footer stays at the grand total.
            const alloc = readRoleAllocation(
              plan.rolePricingBasisByRoleId,
              entry.role_id,
              entry,
              allocationBase
            );
            const overrideSectionKeys = entry.override_section_keys;
            const isOverrideRole = (overrideSectionKeys?.length ?? 0) > 0;
            const effectiveHours = alloc.hours;
            const onshoreHrs = Math.round(effectiveHours * (1 - offshorePercent / 100));
            const offshoreHrs = effectiveHours - onshoreHrs;
            // 0 engine hours (a manually-added role) ⇒ no cost to scale, so cost
            // directly from the carried per-hour rates; otherwise scale the engine cost.
            const onshoreRate = entry.onshore_rate ?? 0;
            const offshoreRate = entry.offshore_rate ?? 0;
            const scale = entry.hours > 0 ? effectiveHours / entry.hours : 0;
            const selectedCost =
              entry.hours > 0
                ? getRoleCost(plan, entry) * scale
                : effectiveHours * roleUnitRate(entry, selectedPricingBasis, offshorePercent);
            const baseSplit = resolveRoleCostSplit(entry, selectedPricingBasis, offshorePercent);
            const costSplit =
              entry.hours > 0
                ? { onshoreCost: baseSplit.onshoreCost * scale, offshoreCost: baseSplit.offshoreCost * scale }
                : { onshoreCost: onshoreHrs * onshoreRate, offshoreCost: offshoreHrs * offshoreRate };

            return (
              <tr key={entry.role_id} className="resource-investment-table__tr">
                <td className="resource-investment-table__td">{entry.role_name}</td>
                <td className="resource-investment-table__td resource-investment-table__td--basis">
                  <PricingBasisControl
                    value={selectedPricingBasis}
                    offshorePercent={offshorePercent}
                    disabled={!onUpdateRolePricingBasis || regenerating || isUpdatingRole}
                    onChange={(basis, pct) => onUpdateRolePricingBasis?.(entry.role_id, basis, pct)}
                  />
                </td>
                <td className="resource-investment-table__td resource-investment-table__td--right resource-investment-table__td--semibold">
                  {effectiveHours}
                  <span className="resource-investment-table__hours-sub">
                    {isHybrid && (
                      <HomeGlobeSplit
                        className="resource-investment-table__hours-sub-split"
                        onshore={`${onshoreHrs}`}
                        offshore={`${offshoreHrs}`}
                      />
                    )}
                    <RoleAllocationControl
                      percent={alloc.percent}
                      isOverridden={alloc.isOverridden}
                      overrideSectionKeys={overrideSectionKeys}
                      saving={updatingRoleAllocationId === entry.role_id}
                      onSave={
                        onUpdateRoleAllocation && !isOverrideRole && !regenerating
                          ? (pct) => onUpdateRoleAllocation(entry.role_id, pct)
                          : undefined
                      }
                    />
                  </span>
                </td>
                {!hideWeeklyAvgColumn && (
                  <td className="resource-investment-table__td resource-investment-table__td--center">
                    <div className="resource-investment-table__chips">
                      {phaseChips.map((chip, idx) => (
                        <Tooltip.Root key={chip.key}>
                          <Tooltip.Trigger asChild>
                            <span
                              className="resource-investment-table__chip"
                              data-phase={chip.key}
                              {...phaseIndexAttr(chip.key, idx)}
                            >
                              {entry.weekly_hours_by_phase[chip.key] ?? 0}
                            </span>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content side="top" sideOffset={4} className="ui-tooltip">
                              {chip.label}: {entry.weekly_hours_by_phase[chip.key] ?? 0} hrs/wk avg
                              <Tooltip.Arrow className="ui-tooltip-arrow" />
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      ))}
                    </div>
                  </td>
                )}
                <td className="resource-investment-table__td resource-investment-table__td--right">
                  ${formatCurrencyAmount(selectedCost)}
                  {isHybrid && (
                    <HomeGlobeSplit
                      className="resource-investment-table__muted-split"
                      onshore={`$${formatCurrencyAmount(costSplit.onshoreCost)}`}
                      offshore={`$${formatCurrencyAmount(costSplit.offshoreCost)}`}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Mobile Card View */}
      <div className="resource-investment-table__role-cards">
        {snap.staffing_plan.map((entry) => {
          const selectedPricingBasis = getRolePricingBasis(plan, entry.role_id);
          const offshorePercent = getRoleOffshorePercent(plan, entry.role_id);
          const isUpdatingRole = updatingRolePricingBasisId === entry.role_id;
          const isHybrid = selectedPricingBasis === 'hybrid';
          const alloc = readRoleAllocation(
            plan.rolePricingBasisByRoleId,
            entry.role_id,
            entry,
            allocationBase
          );
          const overrideSectionKeys = entry.override_section_keys;
          const isOverrideRole = (overrideSectionKeys?.length ?? 0) > 0;
          const effectiveHours = alloc.hours;
          const onshoreHrs = Math.round(effectiveHours * (1 - offshorePercent / 100));
          const offshoreHrs = effectiveHours - onshoreHrs;
          // 0 engine hours (a manually-added role) ⇒ cost directly from the carried
          // per-hour rates; otherwise scale the engine cost.
          const onshoreRate = entry.onshore_rate ?? 0;
          const offshoreRate = entry.offshore_rate ?? 0;
          const scale = entry.hours > 0 ? effectiveHours / entry.hours : 0;
          const selectedCost =
            entry.hours > 0
              ? getRoleCost(plan, entry) * scale
              : effectiveHours * roleUnitRate(entry, selectedPricingBasis, offshorePercent);
          const baseSplit = resolveRoleCostSplit(entry, selectedPricingBasis, offshorePercent);
          const costSplit =
            entry.hours > 0
              ? { onshoreCost: baseSplit.onshoreCost * scale, offshoreCost: baseSplit.offshoreCost * scale }
              : { onshoreCost: onshoreHrs * onshoreRate, offshoreCost: offshoreHrs * offshoreRate };

          return (
            <div key={entry.role_id} className="resource-investment-table__role-card">
              <div className="resource-investment-table__role-card-header">
                <span className="resource-investment-table__role-name">{entry.role_name}</span>
                <span className="resource-investment-table__role-cost">
                  ${formatCurrencyAmount(selectedCost)}
                  {isHybrid && (
                    <HomeGlobeSplit
                      className="resource-investment-table__muted-split"
                      onshore={`$${formatCurrencyAmount(costSplit.onshoreCost)}`}
                      offshore={`$${formatCurrencyAmount(costSplit.offshoreCost)}`}
                    />
                  )}
                </span>
              </div>
              <div className="resource-investment-table__role-card-meta">
                <span className="resource-investment-table__role-hours">{effectiveHours}</span>
                <span className="resource-investment-table__hours-sub">
                  {isHybrid && (
                    <HomeGlobeSplit
                      className="resource-investment-table__hours-sub-split"
                      onshore={`${onshoreHrs}`}
                      offshore={`${offshoreHrs}`}
                    />
                  )}
                  <RoleAllocationControl
                    percent={alloc.percent}
                    isOverridden={alloc.isOverridden}
                    overrideSectionKeys={overrideSectionKeys}
                    saving={updatingRoleAllocationId === entry.role_id}
                    onSave={
                      onUpdateRoleAllocation && !isOverrideRole && !regenerating
                        ? (pct) => onUpdateRoleAllocation(entry.role_id, pct)
                        : undefined
                    }
                  />
                </span>
              </div>
              <div className="resource-investment-table__role-card-controls">
                <PricingBasisControl
                  value={selectedPricingBasis}
                  offshorePercent={offshorePercent}
                  disabled={!onUpdateRolePricingBasis || regenerating || isUpdatingRole}
                  onChange={(basis, pct) => onUpdateRolePricingBasis?.(entry.role_id, basis, pct)}
                />
              </div>
              {!hideWeeklyAvgColumn && (
                <div className="resource-investment-table__role-card-phases">
                  {phaseChips.map((chip, idx) => (
                    <div key={chip.key} className="resource-investment-table__role-phase-item">
                      <span
                        className="resource-investment-table__chip"
                        data-phase={chip.key}
                        {...phaseIndexAttr(chip.key, idx)}
                      >
                        {entry.weekly_hours_by_phase[chip.key] ?? 0}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        className="resource-investment-table__total"
        style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--space-2)' }}
      >
        {overAllocatedPct > 0 && (
          <div className="resource-investment-table__over-alloc" role="status">
            {overAllocatedPct}% over allocated - adjust the role percentages to total 100%.
          </div>
        )}
        {underAllocatedPct > 0 && (
          <div className="resource-investment-table__under-alloc" role="status">
            {underAllocatedPct}% under allocated - add or increase role percentages to total 100%.
          </div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 'var(--space-2)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <span className="resource-investment-table__total-label">
              Total Resource Investment
            </span>
            {!isDiscountFormVisible && onDiscountOverride && (
              <Button
                variant="outline"
                size="sm"
                style={{ height: '24px', fontSize: 'var(--font-size-xs)' }}
                onClick={() => {
                  setIsDiscountFormVisible(true);
                  if (effectiveDiscountPct === 0) {
                    setLocalDiscountPct(defaultDiscountPercentage ?? 0);
                  }
                }}
              >
                + Add discount
              </Button>
            )}
          </div>
          <div className="resource-investment-table__total-values">
            <span className="resource-investment-table__total-hours">
              {snap.totals.total_hours.toLocaleString()} hours
            </span>
            <span className="resource-investment-table__total-sep" aria-hidden="true">
              |
            </span>
            <span className="resource-investment-table__total-cost">
              ${formatCurrencyAmount(displayedTotalCost)}
            </span>
          </div>
        </div>

        {isDiscountFormVisible && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div
                className="resource-investment-table__total-label"
                style={{ fontWeight: 400, color: 'var(--text-secondary)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  Discount Percentage
                  {onDiscountOverride && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <NumberInput
                        min={0}
                        max={100}
                        step={1}
                        style={{
                          width: '80px',
                          padding: 'var(--space-1) var(--space-2)',
                          height: '28px',
                        }}
                        value={localDiscountPct}
                        onChange={(e) => setLocalDiscountPct(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void handleDiscountApply();
                        }}
                      />
                      {isDiscountChanged ? (
                        <>
                          <Button
                            variant="primary"
                            size="sm"
                            style={{ height: '28px' }}
                            onClick={() => void handleDiscountApply()}
                            disabled={isApplyingDiscount}
                          >
                            {isApplyingDiscount ? (
                              <span className="inline-edit__spinner" aria-label="Applying..." />
                            ) : (
                              'Apply'
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            style={{ height: '28px' }}
                            onClick={() => {
                              setLocalDiscountPct(effectiveDiscountPct);
                              if (effectiveDiscountPct === 0) setIsDiscountFormVisible(false);
                            }}
                            disabled={isApplyingDiscount}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          style={{ height: '28px', color: 'var(--text-danger)' }}
                          onClick={() => void handleDiscountRemove()}
                          disabled={isApplyingDiscount}
                        >
                          {isApplyingDiscount ? (
                            <span className="inline-edit__spinner" aria-label="Removing..." />
                          ) : (
                            'Remove'
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                  {!onDiscountOverride && ` (${effectiveDiscountPct}%)`}
                </div>
              </div>
              <span
                className="resource-investment-table__total-cost"
                style={{
                  fontSize: 'var(--font-size-md)',
                  fontWeight: 400,
                  color: 'var(--text-secondary)',
                }}
              >
                -${formatCurrencyAmount(discountAmount)}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 'var(--space-2)',
                borderTop: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)',
                marginTop: 'var(--space-1)',
              }}
            >
              <span className="resource-investment-table__total-label">Estimated Final Cost</span>
              <span
                className="resource-investment-table__total-cost"
                style={{ color: 'var(--accent)' }}
              >
                ${formatCurrencyAmount(displayedTotalCost - discountAmount)}
              </span>
            </div>
          </>
        )}
      </div>
    </>
  );
}
